import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "@tanstack/react-router";
import { XIcon, CookieIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface CookieConsentProps {
  className?: string;
}

const COOKIE_CONSENT_KEY = "clove-cookie-consent";
const COOKIE_CONSENT_VERSION = "1";

export function CookieConsent({ className }: CookieConsentProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const storedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!storedConsent || storedConsent !== COOKIE_CONSENT_VERSION) {
      setIsVisible(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, COOKIE_CONSENT_VERSION);
    setIsVisible(false);
  };

  const handleReject = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, "rejected");
    setIsVisible(false);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className={cn(
      "fixed bottom-0 left-0 right-0 z-50 p-4 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-t",
      className
    )}>
      <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex items-start gap-3 flex-1">
          <CookieIcon className="size-5 mt-0.5 text-muted-foreground flex-shrink-0" />
          <div className="space-y-2">
            <p className="text-sm leading-relaxed">
              <span className="font-medium">we use cookies</span> - our service uses essential cookies to keep you signed in and secure. we don't use tracking or advertising cookies. 
              <Link 
                to="/privacy" 
                className="text-primary hover:underline ml-1"
              >
                learn more in our privacy policy
              </Link>.
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <Button 
                size="sm" 
                onClick={handleAccept}
                className="text-xs bg-accent hover:bg-accent/90"
              >
                accept essential cookies
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleReject}
                className="text-xs hover:bg-muted/50"
              >
                reject non-essential
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleReject}
                className="text-xs text-muted-foreground hover:bg-muted/50"
              >
                <XIcon className="size-3" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}